import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import notify, { MESSAGES } from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import {
  getProcurementMitraFirstItem,
  getProcurementMitraId,
} from '../../../shared/offer-domain/respond';

const useOfferMitraIdentity = (
  procurement,
  { seedProcurementMitraId = null, seedIdOffer = null } = {}
) => {
  const [procurementMitraId, setProcurementMitraId] = useState(null);
  const [idOffer, setIdOffer] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [resolvedKey, setResolvedKey] = useState(null);
  const [error, setError] = useState(null);

  const procurementKey = procurement?.id ?? null;
  const seedKey = `${seedProcurementMitraId ?? ''}:${seedIdOffer ?? ''}`;
  const resolveKey = `${procurementKey ?? ''}:${seedKey}`;
  const hasCandidates = Boolean(
    procurement &&
    ((procurement?.procurementMitra ?? []).some(
      item => item?.id && item.isDeleted !== true
    ) ||
      seedProcurementMitraId)
  );
  const isLoading =
    Boolean(procurementKey && hasCandidates) &&
    (isFetching || resolvedKey !== resolveKey);

  const resolve = useCallback(async () => {
    if (!procurement) {
      return;
    }

    const listed = (procurement?.procurementMitra ?? []).filter(
      item => item?.id && item.isDeleted !== true
    );

    let candidates = listed;
    if (seedProcurementMitraId) {
      const seedInList = listed.find(
        item => String(item.id) === String(seedProcurementMitraId)
      );
      const seedEntry = seedInList ?? {
        id: seedProcurementMitraId,
        idOffer: seedIdOffer,
      };
      candidates = [
        seedEntry,
        ...listed.filter(
          item => String(item.id) !== String(seedProcurementMitraId)
        ),
      ];
    }

    if (!candidates.length) {
      setProcurementMitraId(null);
      setIdOffer(null);
      setResolvedKey(resolveKey);
      return;
    }

    const token = getAuthToken();
    if (!token) {
      notify.error(MESSAGES.authRequired);
      setProcurementMitraId(null);
      setIdOffer(null);
      setResolvedKey(resolveKey);
      return;
    }

    setIsFetching(true);
    setError(null);

    try {
      for (const candidate of candidates) {
        try {
          const response = await axios.get(
            `${api.API_URL}/dmut/get-procurement-mitra/${candidate.id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
              },
            }
          );
          const firstItem = getProcurementMitraFirstItem(response);
          if (!firstItem) continue;

          const resolvedId =
            (candidate?.id != null ? String(candidate.id) : null) ||
            getProcurementMitraId(firstItem) ||
            null;
          const resolvedOfferId =
            firstItem.idOffer ?? firstItem.IdOffer ?? candidate.idOffer ?? null;

          setProcurementMitraId(resolvedId);
          setIdOffer(resolvedOfferId);
          return;
        } catch {
          continue;
        }
      }

      setProcurementMitraId(null);
      setIdOffer(null);
      setError('Offer identity not found for current mitra.');
    } catch (err) {
      const message =
        typeof err === 'string' ? err : 'Failed to resolve offer identity';
      setError(message);
      setProcurementMitraId(null);
      setIdOffer(null);
      notify.apiError(err, 'resolve offer identity');
    } finally {
      setIsFetching(false);
      setResolvedKey(resolveKey);
    }
  }, [procurement, resolveKey, seedIdOffer, seedProcurementMitraId]);

  useEffect(() => {
    resolve();
  }, [resolve]);

  return {
    procurementMitraId,
    idOffer,
    isLoading,
    error,
    refresh: resolve,
  };
};

export default useOfferMitraIdentity;
